import { useParams, Navigate, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, Layers, PanelsTopLeft, Sparkles, Wrench, HardHat, Truck, Check, Phone } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { COMPANY, SERVICES } from "@/lib/constants";
import PageHero from "@/components/site/PageHero";
import CtaBanner from "@/components/site/CtaBanner";

const ICONS = { Building2, Layers, PanelsTopLeft, Sparkles, Wrench, HardHat, Truck };

export default function ServiceDetail() {
    const { slug } = useParams();
    const service = SERVICES.find((s) => s.slug === slug);
    const reduce = useReducedMotion();

    useSeo({
        title: service?.seo_title || "Leistung – Alpen Reinigung",
        description: service?.seo_description || "",
        path: service ? `/leistungen/${service.slug}` : "/leistungen",
        image: service?.image,
    });

    if (!service) return <Navigate to="/leistungen" replace />;

    const Icon = ICONS[service.icon];
    const others = SERVICES.filter((s) => s.slug !== service.slug);

    return (
        <div>
            <PageHero
                eyebrow={service.title}
                title={service.h1}
                lead={service.intro}
                breadcrumbs={[
                    { label: "Leistungen", to: "/leistungen" },
                    { label: service.title },
                ]}
            />

            <section className="relative pb-20 sm:pb-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                        <motion.div
                            initial={reduce ? false : { opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5 }}
                            className="lg:col-span-7"
                        >
                            <div className="rounded-2xl border border-white/[0.10] overflow-hidden card-elevated">
                                <div className="aspect-[16/9] relative">
                                    <img src={service.image} alt={`${service.title} – Alpen Reinigung`} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#07090C]/80 via-transparent to-transparent" />
                                </div>
                            </div>
                            <div className="mt-8">
                                <h2 className="font-heading text-2xl font-semibold text-white">Was ist enthalten?</h2>
                                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {service.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
                                            <span className="mt-0.5 h-6 w-6 rounded-md bg-[rgba(46,168,255,0.12)] border border-[rgba(46,168,255,0.25)] flex items-center justify-center shrink-0">
                                                <Check className="h-3.5 w-3.5 text-[#7BD3FF]" />
                                            </span>
                                            <span className="text-sm text-[#F4F7FB]">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="mt-10">
                                <h2 className="font-heading text-2xl font-semibold text-white">Detailumfang</h2>
                                <ul className="mt-4 space-y-2">
                                    {service.details.map((d) => (
                                        <li key={d} className="text-sm text-[#C9D3E1] flex items-start gap-2">
                                            <span className="mt-2 h-1 w-3 bg-[#2EA8FF] rounded-full shrink-0" aria-hidden="true" />
                                            {d}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>

                        <aside className="lg:col-span-5">
                            <div className="sticky top-24 space-y-6">
                                <div className="rounded-2xl border border-white/[0.10] bg-[rgba(14,20,31,0.78)] backdrop-blur-md p-6 card-elevated">
                                    <div className="flex items-center gap-3">
                                        <span className="h-10 w-10 rounded-lg bg-[rgba(46,168,255,0.12)] border border-[rgba(46,168,255,0.25)] flex items-center justify-center">
                                            {Icon ? <Icon className="h-5 w-5 text-[#7BD3FF]" /> : null}
                                        </span>
                                        <div>
                                            <p className="text-xs font-mono uppercase tracking-[0.18em] text-[#7BD3FF]">Anfragen</p>
                                            <p className="text-white font-heading text-lg">{service.title}</p>
                                        </div>
                                    </div>
                                    <p className="mt-4 text-sm text-[#C9D3E1] leading-relaxed">
                                        Telefon ist am schnellsten – oder füllen Sie einfach das Formular aus. Wir melden uns in der Regel innerhalb eines Werktags zurück.
                                    </p>
                                    <a href={`tel:${COMPANY.phoneTel}`} className="mt-5 inline-flex w-full items-center justify-center gap-2 px-5 h-12 rounded-xl bg-[#2EA8FF] text-[#07090C] font-medium hover:bg-[#7BD3FF] transition-colors btn-glow">
                                        <Phone className="h-4 w-4" />
                                        {COMPANY.phone}
                                    </a>
                                    <Link to="/kontakt" className="mt-3 inline-flex w-full items-center justify-center gap-2 px-5 h-11 rounded-xl border border-white/[0.14] text-[#F4F7FB] hover:border-[rgba(123,211,255,0.55)] hover:bg-[rgba(46,168,255,0.06)] transition-colors">
                                        Angebot anfragen
                                        <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </div>
                                <div className="rounded-2xl border border-white/[0.10] bg-[rgba(18,27,42,0.55)] backdrop-blur-md p-6">
                                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-[#7BD3FF]">Auch interessant</p>
                                    <ul className="mt-3 space-y-2">
                                        {others.map((o) => (
                                            <li key={o.slug}>
                                                <Link to={`/leistungen/${o.slug}`} className="text-sm text-[#C9D3E1] hover:text-white inline-flex items-center gap-1">
                                                    {o.title}
                                                    <ArrowRight className="h-3.5 w-3.5" />
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
            <CtaBanner title={`${service.title} jetzt anfragen`} subtitle="Unverbindlich, transparent und ohne Kleingedrucktes – in Innsbruck und ganz Tirol." />
        </div>
    );
}
